import { SalonDocument } from "../types/salonDocument";
import { sanitizeDownloadBasename } from "./contentDispositionFilename";
import { decodeBlobIfEntireBase64Ascii } from "./decodeBase64ResponseBody";
import {
  blobExtensionMagicMismatch,
  blobStartsLikeJsonOrHtmlDocument,
  suggestedClientDocumentDownloadFilename,
} from "./clientDocumentUploadKey";

type DocFields = Pick<SalonDocument, "documentKey" | "currentContentType">;

/**
 * Turn a raw `GET …/download` body into a typed `Blob` suitable for saving or
 * `URL.createObjectURL` (handles JSON/HTML error bodies and full-body base64 text).
 */
export async function normalizePhidDocumentDownloadBlob(
  doc: DocFields,
  raw: Blob,
  serverFilename?: string,
  responseContentType?: string,
): Promise<Blob> {
  if (await blobStartsLikeJsonOrHtmlDocument(raw)) {
    throw new Error("Download returned JSON or HTML instead of a file");
  }

  const fromHeader = sanitizeDownloadBasename(serverFilename);
  const filename = fromHeader ?? suggestedClientDocumentDownloadFilename(doc);

  let effectiveBlob = raw;
  if (await blobExtensionMagicMismatch(raw, filename)) {
    const decoded = await decodeBlobIfEntireBase64Ascii(raw);
    if (decoded) {
      const ctHint =
        (responseContentType || doc.currentContentType || "")
          .split(";")[0]
          .trim() || "application/octet-stream";
      const candidate = new Blob([Uint8Array.from(decoded)], {
        type: ctHint,
      });
      if (!(await blobExtensionMagicMismatch(candidate, filename))) {
        effectiveBlob = candidate;
      }
    }
  }

  if (await blobExtensionMagicMismatch(effectiveBlob, filename)) {
    throw new Error(
      "Downloaded bytes do not match the file type (check API binary handling)",
    );
  }

  const primary = (
    responseContentType ||
    doc.currentContentType ||
    effectiveBlob.type ||
    ""
  )
    .split(";")[0]
    .trim();

  if (
    primary &&
    (!effectiveBlob.type || effectiveBlob.type === "application/octet-stream")
  ) {
    const buf = await new Response(effectiveBlob).arrayBuffer();
    return new Blob([buf], { type: primary });
  }
  return effectiveBlob;
}
